import { useCallback, useEffect, useState } from 'react';
import { hubService } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import { allowedModelsFor, type PromptRow } from '../../lib/builderForm';
import { fmtTime } from '../../lib/jobView';
import {
  canSaveEdit,
  formatBytes,
  isInline,
  lockConflict,
  lockLabel,
  rechunkRefusal,
  reviewSummary,
  sortForReview,
  THIN_BYTES,
  type SourceRow,
} from '../../lib/sourceView';
import { AiConfigModal } from '../aiconfig';

const input = 'rounded border border-gray-300 px-3 py-2 text-sm';
const PREVIEW_CHARS = 4000;

interface SourceText {
  url: string;
  version: number;
  totalChars: number;
  truncated: boolean;
  text: string;
}

type Dialog =
  | { kind: 'view'; source: SourceRow; text: SourceText | null }
  | { kind: 'edit'; source: SourceRow; text: SourceText | null; draft: string }
  | { kind: 'recrawl'; source: SourceRow; prompt: string; model: string };

/**
 * Knowledge › Pages (A2 step C1, ported from chatbot_front hub/SourcesPanel.js): what the crawl collected, thinnest first,
 * so a bad page is found without opening every one. Per page: view, edit by hand (locks it against the crawler),
 * lock / unlock, re-crawl (optionally another prompt / model), re-chunk. Jobs started here run under Builder › Jobs.
 */
export function PagesPanel({ system, botId, onJobStarted }: { system: string; botId: string; onJobStarted: () => void }) {
  const [rows, setRows] = useState<SourceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [extractModels, setExtractModels] = useState<string[]>([]);

  const k = useCallback(
    <T,>(method: 'GET' | 'POST' | 'PUT', rest: string, body?: unknown) => hubService.knowledge<T>(system, botId, method, rest, body),
    [system, botId],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await k<{ sources: SourceRow[] }>('GET', 'sources');
      setRows(sortForReview(Array.isArray(r?.sources) ? r.sources : []));
    } catch (e) {
      setRows([]);
      setError(errorText(e));
    }
  }, [k]);

  useEffect(() => {
    void load();
    k<PromptRow[]>('GET', 'prompts')
      .then((r) => setExtractModels(Array.isArray(r) ? allowedModelsFor(r, 'extract') : []))
      .catch(() => undefined);
  }, [load, k]);

  const path = (s: SourceRow, rest: string) => `sources/${encodeURIComponent(String(s.sourceId))}/${rest}`;

  async function openText(s: SourceRow, full: boolean, kind: 'view' | 'edit') {
    setDialog(kind === 'view' ? { kind, source: s, text: null } : { kind, source: s, text: null, draft: '' });
    try {
      const t = await k<SourceText>('GET', path(s, `text${full ? '' : `?maxChars=${PREVIEW_CHARS}`}`));
      setDialog(kind === 'view' ? { kind, source: s, text: t } : { kind, source: s, text: t, draft: t.text });
    } catch (e) {
      setDialog(null);
      setError(errorText(e));
    }
  }

  async function saveEdit() {
    if (dialog?.kind !== 'edit') return;
    setBusy(dialog.source.sourceId ?? null);
    try {
      await k('PUT', path(dialog.source, 'text'), { text: dialog.draft });
      setNotice(`Saved and locked ${dialog.source.url}. Re-chunk it to update the bot’s answers.`);
      setDialog(null);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  async function setLock(s: SourceRow, locked: boolean) {
    setBusy(s.sourceId ?? null);
    try {
      await k('POST', path(s, 'lock'), { locked });
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  async function recrawl(overrideLock = false) {
    if (dialog?.kind !== 'recrawl') return;
    const { source, prompt, model } = dialog;
    setBusy(source.sourceId ?? null);
    try {
      await k('POST', path(source, 'recrawl'), { prompt: prompt || undefined, model: model || undefined, overrideLock });
      setDialog(null);
      setNotice(`Re-crawl of ${source.url} started.`);
      onJobStarted();
    } catch (e) {
      const conflict = lockConflict(e);
      if (conflict && !overrideLock) {
        const when = conflict.editedAt ? ` on ${fmtTime(conflict.editedAt)}` : '';
        if (window.confirm(`This page was edited by hand (v${conflict.version}${when}). Re-crawling replaces that edit and unlocks it. Continue?`)) {
          setBusy(null);
          await recrawl(true);
          return;
        }
      } else {
        setError(errorText(e));
      }
    } finally {
      setBusy(null);
    }
  }

  async function rechunk(s: SourceRow) {
    setBusy(s.sourceId ?? null);
    setError(null);
    try {
      await k('POST', path(s, 'rechunk'), {});
      setNotice(`Re-chunk of ${s.url} started. If it makes fewer chunks than now, approve it under Builder › Jobs.`);
      onJobStarted();
    } catch (e) {
      const refusal = rechunkRefusal(e);
      // A refusal is not a failure: nothing was spent, and the text says what to change.
      if (refusal) setNotice(refusal.text);
      else setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) return <p className="text-sm text-gray-500">Loading…</p>;
  const sum = reviewSummary(rows);

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div>
        <h2 className="font-medium text-gray-900">Pages</h2>
        <p className="text-sm text-gray-600">
          What the crawl collected for this bot, thinnest first. Fix a page by hand, or re-crawl / re-chunk just that page instead of the whole site.
        </p>
        {rows.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {sum.total} page(s){sum.empty ? ` · ${sum.empty} collected nothing` : ''}
            {sum.thin ? ` · ${sum.thin} under ${THIN_BYTES} B (probably not content)` : ''}
            {sum.inactive ? ` · ${sum.inactive} not in the active snapshot` : ''}
            {sum.inline ? ` · ${sum.inline} uploaded document(s)` : ''}
          </p>
        )}
      </div>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2 flex justify-between gap-2">
          <span>{error}</span>
          <button type="button" className="text-gray-600 hover:underline shrink-0" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {notice && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2 flex justify-between gap-2">
          <span>{notice}</span>
          <button type="button" className="text-gray-600 hover:underline shrink-0" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No pages yet — run a build on the Builder tab.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">Page</th>
                <th className="py-2 pr-3">Size</th>
                <th className="py-2 pr-3">Version</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const label = lockLabel(s);
                const thin = s.bytes != null && s.bytes < THIN_BYTES;
                const disabled = busy === s.sourceId;
                return (
                  <tr key={s.sourceId ?? s.url} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 max-w-[28rem]">
                      <div className="truncate" title={s.url}>
                        {s.url}
                      </div>
                      <div className="text-xs space-x-2">
                        {label && <span className="text-amber-700">🔒 {label}</span>}
                        {s.inActiveSnapshot === false && <span className="text-gray-500">not in the active snapshot</span>}
                      </div>
                    </td>
                    <td className={`py-2 pr-3 ${s.bytes == null || thin ? 'text-red-600' : ''}`}>{formatBytes(s.bytes)}</td>
                    <td className="py-2 pr-3 text-gray-600">
                      {s.version != null ? `v${s.version}` : '—'}
                      {s.versions && s.versions > 1 ? <span className="text-xs text-gray-400"> of {s.versions}</span> : null}
                    </td>
                    <td className="py-2 whitespace-nowrap space-x-3 text-right">
                      <button type="button" className="text-blue-600 hover:underline" disabled={s.version == null} onClick={() => void openText(s, false, 'view')}>
                        View
                      </button>
                      <button type="button" className="text-blue-600 hover:underline" disabled={s.version == null || disabled} onClick={() => void openText(s, true, 'edit')}>
                        Edit
                      </button>
                      <button type="button" className="text-blue-600 hover:underline" disabled={disabled} onClick={() => void setLock(s, !s.editLocked)}>
                        {s.editLocked ? 'Unlock' : 'Lock'}
                      </button>
                      {!isInline(s.url) && (
                        <button
                          type="button"
                          className="text-blue-600 hover:underline"
                          disabled={disabled}
                          onClick={() => setDialog({ kind: 'recrawl', source: s, prompt: '', model: '' })}
                        >
                          Re-crawl
                        </button>
                      )}
                      <button type="button" className="text-blue-600 hover:underline" disabled={disabled || s.version == null} onClick={() => void rechunk(s)}>
                        Re-chunk
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.kind === 'view' && (
        <AiConfigModal title={dialog.source.url} onClose={() => setDialog(null)} maxWidth="xl">
          {!dialog.text ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                v{dialog.text.version} · {dialog.text.totalChars.toLocaleString('en')} characters
                {dialog.text.truncated ? ` · first ${PREVIEW_CHARS.toLocaleString('en')} shown` : ''}
              </p>
              <pre className="text-xs bg-gray-50 border rounded p-2 whitespace-pre-wrap max-h-[60vh] overflow-auto">{dialog.text.text}</pre>
              {dialog.text.truncated && (
                <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => void openText(dialog.source, true, 'view')}>
                  Load the full text
                </button>
              )}
            </>
          )}
        </AiConfigModal>
      )}

      {dialog?.kind === 'edit' && (
        <AiConfigModal title={`Edit — ${dialog.source.url}`} onClose={() => setDialog(null)} maxWidth="xl">
          {!dialog.text ? (
            <p className="text-sm text-gray-500">Loading the full text…</p>
          ) : (
            (() => {
              const check = canSaveEdit(dialog.text, dialog.draft);
              return (
                <>
                  <p className="text-xs text-gray-500">Saving replaces this page’s text and locks it, so the next build keeps your version.</p>
                  <textarea
                    className={`w-full ${input} font-mono text-xs`}
                    rows={20}
                    value={dialog.draft}
                    onChange={(e) => setDialog({ ...dialog, draft: e.target.value })}
                  />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                      disabled={!check.ok || busy === dialog.source.sourceId}
                      onClick={() => void saveEdit()}
                    >
                      Save and lock
                    </button>
                    {check.reason && <span className="text-xs text-red-600">{check.reason}</span>}
                  </div>
                </>
              );
            })()
          )}
        </AiConfigModal>
      )}

      {dialog?.kind === 'recrawl' && (
        <AiConfigModal title={`Re-crawl — ${dialog.source.url}`} onClose={() => setDialog(null)} maxWidth="lg">
          <p className="text-xs text-gray-500">Re-reads only this page (one LLM call). Leave both empty to use the bot’s settings.</p>
          <textarea
            className={`w-full ${input} text-xs`}
            rows={5}
            placeholder="Extraction prompt for this page only (optional)"
            value={dialog.prompt}
            onChange={(e) => setDialog({ ...dialog, prompt: e.target.value })}
          />
          <label className="text-sm flex items-center gap-2">
            Model
            <select className={input} value={dialog.model} onChange={(e) => setDialog({ ...dialog, model: e.target.value })}>
              <option value="">the bot’s</option>
              {extractModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={busy === dialog.source.sourceId}
            onClick={() => void recrawl()}
          >
            Re-crawl this page
          </button>
        </AiConfigModal>
      )}
    </section>
  );
}
